import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../../services/api';
import { formatTimeSaudi } from '../../utils/saudiTime';

export default function OrderReceiverOrders() {
  const [orders, setOrders] = useState([]);
  const [slotCounts, setSlotCounts] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [markingReadyId, setMarkingReadyId] = useState(null);

  const loadOrders = async () => {
    setError('');
    setLoading(true);
    try {
      const [ordersRes, slotCountsRes] = await Promise.all([
        apiService.orders.getSalesOrders(),
        apiService.orders.getSalesOrderSlotCounts(),
      ]);
      if (Array.isArray(ordersRes.data)) setOrders(ordersRes.data);
      setSlotCounts(slotCountsRes.data?.countsBySlot || {});
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const slotSummary = useMemo(() => {
    const counts = slotCounts || {};
    const noSlotCount = Number(counts[''] || 0);
    const totalOrders = Object.values(counts).reduce((sum, v) => sum + (Number(v) || 0), 0);
    const orderedSlots = Object.entries(counts)
      .filter(([k, v]) => k && Number(v) > 0)
      .map(([label, count]) => ({ label, count: Number(count) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { orderedSlots, noSlotCount, totalOrders };
  }, [slotCounts]);

  const filteredOrders = selectedSlot
    ? orders.filter((o) => (o.deliverySlot || '') === selectedSlot)
    : orders;

  const updateStatus = async (orderId, status) => {
    setUpdatingId(orderId);
    setError('');
    try {
      await apiService.orders.updateSalesOrderStatus(orderId, status);
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update');
    } finally {
      setUpdatingId(null);
    }
  };

  const markAsReady = async (orderId) => {
    setMarkingReadyId(orderId);
    setError('');
    try {
      await apiService.orders.markSalesOrderReady(orderId);
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to mark order as ready');
    } finally {
      setMarkingReadyId(null);
    }
  };

  const formatDateTime = formatTimeSaudi;

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2 sm:mb-4">Orders</h1>
        <p className="text-gray-600 text-sm mb-4 sm:mb-6">
          Confirm or reject orders sent by sales. Showing today&apos;s orders only.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">{error}</div>
        )}

        {!loading && slotCounts && (
          <div className="mb-5">
            <div className="text-sm font-semibold text-gray-700 mb-2">Slot wise number of orders</div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setSelectedSlot('')}
                className={`w-full flex items-center justify-between border rounded-lg px-4 py-3 text-left ${
                  !selectedSlot ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span className="font-medium text-gray-800">All Orders</span>
                <span className="font-bold text-gray-800">{slotSummary.totalOrders} Orders</span>
              </button>
              {slotSummary.orderedSlots.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => setSelectedSlot(s.label)}
                  className={`w-full flex items-center justify-between border rounded-lg px-4 py-3 text-left ${
                    selectedSlot === s.label ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-medium text-gray-800">{s.label}</span>
                  <span className="font-bold text-red-600">{s.count} Orders</span>
                </button>
              ))}
              {slotSummary.noSlotCount > 0 && (
                <div className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 bg-gray-50">
                  <span className="font-medium text-gray-700">No slot selected</span>
                  <span className="font-bold text-gray-700">{slotSummary.noSlotCount} Orders</span>
                </div>
              )}
            </div>
          </div>
        )}

        {loading && <div className="py-8 text-center text-gray-500">Loading…</div>}
        {!loading && filteredOrders.length === 0 && (
          <div className="py-8 text-center text-gray-500 border border-gray-200 rounded-lg">No orders yet.</div>
        )}
        {!loading && filteredOrders.length > 0 && (
          <div className="space-y-4">
            {filteredOrders.map((order) => {
              const itemCount = (order.items || []).reduce((sum, i) => sum + (i.orderQty || 0), 0);
              const isPending = order.status === 'Pending' || order.status === 'Sent to Central Kitchen';
              return (
                <div
                  key={order._id}
                  className="border border-gray-200 rounded-lg p-3 sm:p-4 border-l-4 border-l-blue-400"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-800 text-base sm:text-lg">{order.orderNo}</div>
                        {order.customerName && (
                          <div className="text-sm sm:text-base text-gray-600 mt-0.5">
                            Customer: {order.customerName}
                          </div>
                        )}
                        {order.customerPhone && (
                          <div className="text-sm sm:text-base text-gray-600 mt-0.5">
                            Phone: {order.customerPhone}
                          </div>
                        )}
                        {order.deliverySlot && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <span className="text-base sm:text-lg font-bold text-gray-800">Delivery Slot: </span>
                            <span className="text-base sm:text-lg font-bold text-blue-700">{order.deliverySlot}</span>
                          </div>
                        )}
                        <div className="text-sm sm:text-base text-gray-500 mt-1">
                          {formatDateTime(order.dateTime)} · {itemCount} item(s)
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className={`inline-block px-3 py-1.5 rounded-full text-sm font-medium ${
                            order.status === 'Received' && order.paymentReceived
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              : order.status === 'Received' && !order.paymentReceived
                              ? 'bg-amber-100 text-amber-800 border border-amber-200'
                              : order.status === 'Rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}
                        >
                          {order.status === 'Sent to Central Kitchen'
                            ? 'Pending'
                            : order.status === 'Received' && order.paymentReceived
                            ? 'Payment received'
                            : order.status === 'Received' && !order.paymentReceived
                            ? 'Pending payment'
                            : order.status}
                        </span>
                        {order.isReady && (
                          <span className="inline-block px-3 py-1.5 rounded-full text-sm font-bold bg-green-100 text-green-800 border border-green-200">
                            ✓ Ready
                          </span>
                        )}
                        {isPending && (
                          <>
                            <button
                              type="button"
                              disabled={updatingId === order._id}
                              onClick={() => updateStatus(order._id, 'Received')}
                              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm py-2.5 px-4 rounded-lg min-h-[44px] touch-manipulation"
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              disabled={updatingId === order._id}
                              onClick={() => updateStatus(order._id, 'Rejected')}
                              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm py-2.5 px-4 rounded-lg min-h-[44px] touch-manipulation"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {order.status === 'Received' && !order.isReady && (
                          <button
                            type="button"
                            disabled={markingReadyId === order._id}
                            onClick={() => markAsReady(order._id)}
                            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 px-4 rounded-lg min-h-[44px] touch-manipulation"
                          >
                            {markingReadyId === order._id ? 'Marking…' : 'Mark as Ready'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {order.items && order.items.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                        {order.items.map((item, idx) => {
                          const qty = Number(item.orderQty) || 0;
                          const unitPrice = Number(item.unitPrice) || 0;
                          const lineTotal = qty * unitPrice;
                          return (
                            <li key={idx} className="flex justify-between gap-3">
                              <span>
                                {item.itemName} × {qty}
                              </span>
                              <span className="font-medium tabular-nums whitespace-nowrap">
                                {unitPrice > 0 ? `SAR ${lineTotal.toFixed(2)}` : '—'}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      {order.totalAmount != null && Number(order.totalAmount) > 0 && (
                        <div className="flex justify-between text-base font-semibold text-gray-800 mt-3 pt-2 border-t border-gray-100">
                          <span>Total</span>
                          <span className="tabular-nums">SAR {Number(order.totalAmount).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
